import axios from 'axios';
import { config } from '../config/env.js';

// Get API base URL from environment configuration
const API_BASE_URL = config.apiBaseUrl;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and redirect to login
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  // register: (userData) => api.post('/auth/register', userData), // TEMPORARILY DISABLED
  getMe: () => api.get('/auth/me'),
};


// Items API (based on Item model)
export const itemsAPI = {
  getAll: (params = {}) => api.get('/items', { params }),
  getById: (id, params = {}) => api.get('/items', { params: { search: id, ...params } }),
  getStockHistory: (id) => api.get(`/items/${id}/stock-history`),
  getByBarcode: (barcode) => api.get(`/items/barcode/${barcode}`),
  getByProductId: (productId) => api.get(`/items/product/${productId}`),
  create: (item) => api.post('/items', item),
  update: (id, item) => api.put(`/items/${id}`, item),
  delete: (id) => api.delete(`/items/${id}`),
  search: (query, params = {}) => api.get('/items', { params: { search: query, ...params } }),
  getByBrand: (brand) => api.get('/items/brand', { params: { brand } }),
  getLowStock: (threshold = 10) => api.get('/items/low-stock', { params: { threshold } }),
  getExpiringSoon: (days = 30) => api.get('/items/expiring', { params: { days } }),
  updateStock: (id, quantity) => api.patch(`/items/${id}/stock`, { quantity }),
  getRelatedByBarcode: (barcode) => api.get(`/items/by-barcode/${barcode}/related`),
  getStats: () => api.get('/items/stats'),
  syncAll: () => api.post('/items/sync-all'),
};

// Legacy Products API (for backward compatibility)
export const productsAPI = {
  getAll: (params = {}) => itemsAPI.getAll(params),
  getById: (id) => itemsAPI.getById(id),
  getByBarcode: (barcode) => itemsAPI.getByBarcode(barcode),
  search: (query) => itemsAPI.search(query),
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (customer) => api.post('/customers', customer),
  update: (id, customer) => api.put(`/customers/${id}`, customer),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (order) => api.post('/orders', order),
  update: (id, order) => api.put(`/orders/${id}`, order),
  checkout: (cart, paymentMethod, customerId = null) => 
    api.post('/pos/checkout', { 
      items: cart.items,
      payment_method: paymentMethod, 
      customer_id: customerId,
      tax_amount: cart.tax_amount,
      discount_amount: cart.discount_amount
    }),
};

// Sales API
export const salesAPI = {
  list: (params = {}) => api.get('/sales', { params }),
  listUnreturned: (params = {}) => api.get('/sales/unreturned', { params }),
  create: (payload) => api.post('/sales', payload),
  getById: (id) => api.get(`/sales/${id}`),
  updatePaymentMode: (id, data) => api.patch(`/sales/${id}/payment-mode`, data),
};

// Sale Returns API
export const saleReturnsAPI = {
  list: (params = {}) => api.get('/sale-returns', { params }),
  get: (id) => api.get(`/sale-returns/${id}`),
  getBySale: (saleId) => api.get(`/sale-returns/return-for-sale/${saleId}`),
  getByExchange: (exchangeId) => api.get(`/sale-returns/return-for-exchange/${exchangeId}`),
  getByWebOrder: (brand, woocommerceId) => api.get(`/sale-returns/return-for-web-order/${brand}/${woocommerceId}`),
  create: (payload) => api.post('/sale-returns', payload),
  update: (id, payload) => api.put(`/sale-returns/${id}`, payload),
  remove: (id) => api.delete(`/sale-returns/${id}`),
  listDetails: (id) => api.get(`/sale-returns/${id}/details`),
  addDetail: (id, payload) => api.post(`/sale-returns/${id}/details`, payload),
  updateDetail: (detailId, payload) => api.put(`/sale-returns/details/${detailId}`, payload),
  removeDetail: (detailId) => api.delete(`/sale-returns/details/${detailId}`),
};

// Analytics API
export const analyticsAPI = {
  getTodayStats: (params = {}) => api.get('/analytics/today-stats', { params }),
  getPopularItems: (params = {}) => api.get('/analytics/popular-items', { params }),
  getSalesTrend: (params = {}) => api.get('/analytics/sales-trend', { params }),
  getRevenueBreakdown: (params = {}) => api.get('/analytics/revenue-breakdown', { params }),
  getDailySalesBreakdown: (params = {}) => api.get('/analytics/daily-sales-breakdown', { params }),
  getMonthlyStats: (params = {}) => api.get('/analytics/monthly-stats', { params }),
  exportSales: (params = {}) => api.get('/analytics/export-sales', { params }),
  exportReturns: (params = {}) => api.get('/analytics/export-returns', { params }),
  exportExchanges: (params = {}) => api.get('/analytics/export-exchanges', { params }),
};

// Purchases API
export const purchasesAPI = {
  list: (params = {}) => api.get('/purchases', { params }),
  get: (id) => api.get(`/purchases/${id}`),
  create: (payload) => api.post('/purchases', payload),
  update: (id, payload) => api.put(`/purchases/${id}`, payload),
  remove: (id) => api.delete(`/purchases/${id}`),
  publish: (id) => api.post(`/purchases/${id}/publish`),
  listDetails: (id) => api.get(`/purchases/${id}/details`),
  addDetail: (id, payload) => api.post(`/purchases/${id}/details`, payload),
  updateDetail: (detailId, payload) => api.put(`/purchases/details/${detailId}`, payload),
  removeDetail: (detailId) => api.delete(`/purchases/details/${detailId}`),
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/purchases/import/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Purchase Returns API
export const purchaseReturnsAPI = {
  list: (params = {}) => api.get('/purchase-returns', { params }),
  get: (id) => api.get(`/purchase-returns/${id}`),
  create: (payload) => api.post('/purchase-returns', payload),
  remove: (id) => api.delete(`/purchase-returns/${id}`),
};

// Damages API: damaged stock and the dispatches that send it to the warehouse
export const damagesAPI = {
  listOpen: () => api.get('/damages/open'),
  create: (payload) => api.post('/damages/', payload),
  remove: (id) => api.delete(`/damages/${id}`),
  dispatch: (payload) => api.post('/damages/dispatches', payload),
  listDispatches: () => api.get('/damages/dispatches'),
  getDispatch: (id) => api.get(`/damages/dispatches/${id}`),
};

// Exchanges API
export const exchangesAPI = {
  list: (params = {}) => api.get('/exchanges', { params }),
  get: (id) => api.get(`/exchanges/${id}`),
  create: (payload) => api.post('/exchanges', payload),
  update: (id, payload) => api.put(`/exchanges/${id}`, payload),
  remove: (id) => api.delete(`/exchanges/${id}`),
  retrySync: (id) => api.post(`/exchanges/${id}/retry-sync`),
};

// WooCommerce API
export const woocommerceAPI = {
  getBrands: () => api.get('/woocommerce/brands'),
  getOrder: (brand, orderId) => api.get(`/woocommerce/orders/${brand}/${orderId}`),
  getCapabilities: () => api.get('/woocommerce/capabilities'),
};

// Business Information API
export const businessInfoAPI = {
  get: () => api.get('/business-information'),
  getPublic: () => api.get('/business-information/public'),
  update: (payload) => {
    // Handle file upload with FormData
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.keys(payload).forEach(key => {
      if (key === 'Logo' && payload[key] instanceof File) {
        formData.append('Logo', payload[key]);
      } else if (Array.isArray(payload[key])) {
        // Multipart has no list type, so every mailing list goes as JSON and the
        // backend parses it back. Appending the array raw would let FormData
        // stringify it as a bare comma-joined string instead.
        formData.append(key, JSON.stringify(payload[key]));
      } else if (key === 'SMTPConfig' && payload[key] && typeof payload[key] === 'object') {
        formData.append('SMTPConfig', JSON.stringify(payload[key]));
      } else if (payload[key] !== null && payload[key] !== undefined) {
        formData.append(key, payload[key]);
      }
    });
    
    return api.put('/business-information', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getDiscountLimits: () => api.get('/business-information/discount-limits'),
  requestDiscountApproval: (payload) => api.post('/business-information/discount-approval', payload),
  verifyOTP: (payload) => api.post('/business-information/verify-otp', payload),
  // Cart review: available to every signed-in user, unlike the admin-only
  // business information endpoints above.
  getCartReviewConfig: () => api.get('/business-information/cart-review-config'),
  sendCartReviewEmail: (payload) => api.post('/business-information/cart-review-email', payload),
  getLogo: () => {
    // Create a direct URL to the logo endpoint
    const logoUrl = `${config.apiBaseUrl}/business-information/logo`;
    return Promise.resolve({ data: logoUrl });
  },
};

// Optional features this branch has switched on (branches.json `features`). Public, so it
// can load before login without tripping the 401 redirect above. The timeout turns a hung
// request into a failure that useBranchFeatures retries.
export const featuresAPI = {
  get: () => api.get('/features', { timeout: 10000 }),
};

// Closing API
export const closingsAPI = {
  list: (params = {}) => api.get('/closings', { params }),
  get: (id) => api.get(`/closings/${id}`),
  create: (payload) => api.post('/closings', payload),
  update: (id, payload) => api.put(`/closings/${id}`, payload),
  calculate: (payload) => api.post('/closings/calculate', payload),
  getPrevious: (date) => api.get(`/closings/previous/${date}`),
};

// Expense API
export const expensesAPI = {
  list: (params = {}) => api.get('/expenses', { params }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (payload) => api.post('/expenses', payload),
  update: (id, payload) => api.put(`/expenses/${id}`, payload),
  remove: (id) => api.delete(`/expenses/${id}`),
};

// Reports API (customisable reporting engine)
export const reportsAPI = {
  getDatasets: () => api.get('/reports/datasets'),
  list: (params = {}) => api.get('/reports/', { params }),
  get: (id) => api.get(`/reports/${id}`),
  create: (payload) => api.post('/reports/', payload),
  update: (id, payload) => api.put(`/reports/${id}`, payload),
  remove: (id) => api.delete(`/reports/${id}`),
  preview: (definition, params) => api.post('/reports/preview', { definition, params }),
  exportPreview: (definition, params, format) =>
    api.post('/reports/export', { definition, params, format }, { responseType: 'blob' }),
  run: (id, params) => api.post(`/reports/${id}/run`, params),
  export: (id, format, params) =>
    api.post(`/reports/${id}/export`, params, { params: { format }, responseType: 'blob' }),
  sqlPreview: (sql, params) => api.post('/reports/sql/preview', { sql, params }),
  sqlExport: (sql, params, format) =>
    api.post('/reports/sql/export', { sql, params, format }, { responseType: 'blob' }),
};

// Analytics 2 dashboard (built on the reporting engine)
export const analytics2API = {
  getDashboard: (params = {}) => api.get('/analytics2/dashboard', { params }),
};

export default api;
